import { useInitialLoading } from '../hooks/useInitialLoading';
import { ProtectedImage } from '../components/ProtectedFile';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function QaTicketDetail() {
  const navigate = useNavigate();
  const { projectId, subprojectId, sspId, ticketId } = useParams();
  const companyId = localStorage.getItem('selectedCompanyId');

  const [project, setProject] = useState(null);
  const [subproject, setSubproject] = useState(null);
  const [ssp, setSsp] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  // Edit
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', description: '', priority: '' });

  // Comment
  const [commentText, setCommentText] = useState('');

  // Report Again
  const [showReportAgain, setShowReportAgain] = useState(false);
  const [reportFiles, setReportFiles] = useState([]);
  const reportFileRef = useRef();

  // Preview
  const [previewImg, setPreviewImg] = useState(null);

  const base = `${API}/companies/${companyId}/qa/projects/${projectId}/subprojects/${subprojectId}/sub-subprojects/${sspId}/tickets/${ticketId}`;

  useEffect(() => { if (companyId) fetchTicket(); }, [companyId, ticketId]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchTicket = async () => {
    setLoading(true);
    const res = await authFetch(base);
    if (res && res.ok) {
      const data = await res.json();
      setProject(data.project || null);
      setSubproject(data.subproject || null);
      setSsp(data.sub_subproject || null);
      setTicket(data.ticket || null);
    }
    setLoading(false);
  };

  const saveEdit = async () => {
    const res = await authFetch(base, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editForm)
    });
    if (res && res.ok) { setEditing(false); fetchTicket(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const markSolved = async () => {
    const res = await authFetch(`${base}/solve`, { method: 'POST' });
    if (res && res.ok) fetchTicket();
  };

  const reportAgain = async () => {
    const fd = new FormData();
    reportFiles.forEach(f => fd.append('evidence[]', f));

    const token = localStorage.getItem('token');
    const res = await fetch(`${base}/report-again`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd
    });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
    if (res.ok) { setShowReportAgain(false); setReportFiles([]); fetchTicket(); }
    else { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const archiveTicket = async () => {
    if (!window.confirm('Archive this ticket?')) return;
    const res = await authFetch(`${base}/archive`, { method: 'POST' });
    if (res && res.ok) fetchTicket();
  };

  const restoreTicket = async () => {
    const res = await authFetch(`${base}/restore`, { method: 'POST' });
    if (res && res.ok) fetchTicket();
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    const res = await authFetch(`${base}/comments`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment: commentText })
    });
    if (res && res.ok) { setCommentText(''); fetchTicket(); }
  };

  const priorityColor = (p) => ({ critical: '#e74c3c', high: '#e67e22', medium: '#f39c12', low: '#95a5a6' }[p] || '#95a5a6');

  if (!companyId) return <div style={s.page}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={s.page}><p>Loading ticket...</p></div>;
  if (!ticket) return <div style={s.page}><p>Ticket not found.</p></div>;

  const wsPath = `/company/qa/projects/${projectId}/subprojects/${subprojectId}/workspace/${sspId}`;

  return (
    <div style={s.page}>
      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <span style={s.breadLink} onClick={() => navigate('/company/qa')}>QA Management</span>
        <span style={s.breadSep}>/</span>
        <span style={s.breadLink} onClick={() => navigate(`/company/qa/projects/${projectId}`)}>{project?.name}</span>
        <span style={s.breadSep}>/</span>
        <span style={s.breadLink} onClick={() => navigate(`/company/qa/projects/${projectId}/subprojects/${subprojectId}`)}>{subproject?.name}</span>
        <span style={s.breadSep}>/</span>
        <span style={s.breadLink} onClick={() => navigate(wsPath)}>{ssp?.name}</span>
        <span style={s.breadSep}>/</span>
        <span>#{String(ticket.ticket_number).padStart(3, '0')}</span>
      </div>

      {/* Header */}
      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20 }}>
              <span style={{ color: '#888', marginRight: 8 }}>#{String(ticket.ticket_number).padStart(3, '0')}</span>
              {editing ? (
                <input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                       style={{ ...s.input, display: 'inline', width: 'auto', minWidth: 300 }} />
              ) : ticket.title}
            </h2>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <span style={{ ...s.badge, backgroundColor: priorityColor(ticket.priority) }}>
                {editing ? (
                  <select value={editForm.priority} onChange={e => setEditForm({ ...editForm, priority: e.target.value })}
                          style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                    {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                  </select>
                ) : ticket.priority.toUpperCase()}
              </span>
              <span style={{ ...s.badge, backgroundColor: ticket.status === 'solved' ? '#27ae60' : '#e74c3c' }}>{ticket.status.toUpperCase()}</span>
              {ticket.is_archived && <span style={{ ...s.badge, backgroundColor: '#7f8c8d' }}>ARCHIVED</span>}
              <span style={{ fontSize: 12, color: '#888', alignSelf: 'center' }}>Reported {ticket.report_count}x</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {editing ? (
              <>
                <button onClick={saveEdit} style={s.btnPrimary}>Save</button>
                <button onClick={() => setEditing(false)} style={s.btnCancel}>Cancel</button>
              </>
            ) : (
              <>
                <button onClick={() => { setEditing(true); setEditForm({ title: ticket.title, description: ticket.description, priority: ticket.priority }); }} style={s.btnSm}>Edit</button>
                <button onClick={() => setShowReportAgain(true)} style={{ ...s.btnSm, background: '#e74c3c', color: '#fff' }}>Report Again</button>
                {ticket.status === 'unsolved' ? (
                  <button onClick={markSolved} style={{ ...s.btnSm, background: '#27ae60', color: '#fff' }}>Mark as Solved</button>
                ) : null}
                {ticket.is_archived ? (
                  <button onClick={restoreTicket} style={s.btnSm}>Restore</button>
                ) : (
                  <button onClick={archiveTicket} style={{ ...s.btnSm, color: '#e74c3c' }}>Archive</button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>Description</h3>
        {editing ? (
          <textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                    style={{ ...s.input, minHeight: 100 }} />
        ) : (
          <p style={{ whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.6 }}>{ticket.description}</p>
        )}
      </div>

      {/* Chronological Lifecycle */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>Lifecycle</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(ticket.occurrences || []).map((occ, i) => (
            <div key={occ.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{
                minWidth: 10, minHeight: 10, maxWidth: 10, maxHeight: 10, borderRadius: '50%', marginTop: 5,
                backgroundColor: occ.type === 'reported' ? '#e74c3c' : '#27ae60'
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{new Date(occ.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <span style={{ ...s.badge, backgroundColor: occ.type === 'reported' ? '#e74c3c' : '#27ae60', fontSize: 10 }}>
                    {occ.type === 'reported' ? 'REPORTED' : 'QA APPROVED'}
                  </span>
                  {occ.user && <span style={{ fontSize: 12, color: '#888' }}>by {occ.user.name}</span>}
                </div>
                {/* Evidence for this occurrence */}
                {occ.evidence && occ.evidence.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {occ.evidence.map(ev => (
                      <ProtectedImage key={ev.id} path={ev.file_path} alt={ev.original_name}
                           style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: '1px solid #ddd' }}
                           onPreview={setPreviewImg} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comments */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>Internal Comments</h3>
        {(ticket.comments || []).length === 0 && <p style={{ color: '#999', fontSize: 13 }}>No comments yet.</p>}
        {(ticket.comments || []).map(c => (
          <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 12 }}>
              <strong>{c.user?.name || 'Unknown'}</strong>
              <span style={{ color: '#999', marginLeft: 8 }}>{new Date(c.created_at).toLocaleString()}</span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 14, whiteSpace: 'pre-wrap' }}>{c.comment}</p>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <input value={commentText} onChange={e => setCommentText(e.target.value)} style={{ ...s.input, flex: 1, marginBottom: 0 }}
                 placeholder="Add a comment..." onKeyDown={e => e.key === 'Enter' && addComment()} />
          <button onClick={addComment} style={s.btnPrimary}>Post</button>
        </div>
      </div>

      {/* Activity History */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>Activity History</h3>
        {(ticket.activities || []).length === 0 && <p style={{ color: '#999', fontSize: 13 }}>No activity recorded.</p>}
        {(ticket.activities || []).map(a => (
          <div key={a.id} style={{ padding: '6px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
            <span style={{ color: '#888' }}>{new Date(a.created_at).toLocaleString()}</span>
            <span style={{ margin: '0 8px' }}>—</span>
            <span>{a.description}</span>
            {a.user && <span style={{ color: '#888' }}> by {a.user.name}</span>}
          </div>
        ))}
      </div>

      {/* Report Again Modal */}
      {showReportAgain && (
        <div style={s.overlay} onClick={() => setShowReportAgain(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>Report Again</h3>
            <p style={{ fontSize: 13, color: '#666' }}>This will add another reporting occurrence to ticket #{String(ticket.ticket_number).padStart(3, '0')}.</p>
            <label style={s.label}>New Evidence (optional)</label>
            <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple ref={reportFileRef}
                   onChange={e => setReportFiles([...e.target.files])} style={{ marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={reportAgain} style={{ ...s.btnPrimary, background: '#e74c3c' }}>Report Again</button>
              <button onClick={() => setShowReportAgain(false)} style={s.btnCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview */}
      {previewImg && (
        <div style={s.overlay} onClick={() => setPreviewImg(null)}>
          <img src={previewImg} alt="Preview" style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }} />
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1000, margin: '0 auto' },
  breadcrumb: { fontSize: 13, color: '#888', marginBottom: 12 },
  breadLink: { color: '#3498db', cursor: 'pointer' },
  breadSep: { margin: '0 6px' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, color: '#fff', fontSize: 11, fontWeight: 700 },
  section: { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 16 },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12, borderBottom: '2px solid #3498db', paddingBottom: 6, marginTop: 0 },
  input: { padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: '100%', boxSizing: 'border-box', marginBottom: 8 },
  btnPrimary: { padding: '8px 16px', background: '#3498db', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnCancel: { padding: '8px 16px', background: '#ecf0f1', color: '#2c3e50', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  btnSm: { padding: '6px 12px', background: '#ecf0f1', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 450 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 8 },
};